/**
 * Field-level diffs from opportunity audit before/after snapshots.
 */

import { formatCodeLabel } from '@/lib/format-code-label'
import { formatDate } from '@/lib/format-date'
import { formatMoney } from '@/lib/format-money'

const SKIP_KEYS = new Set(['last_updated_at', 'version', 'currency'])

const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  customer_id: 'Customer',
  owner_id: 'Owner',
  stage_id: 'Stage',
  start_date: 'Start date',
  end_date: 'End date',
  forecast_driver_code: 'Forecast driver',
  billing_type_code: 'Billing type',
  revenue_pattern_code: 'Revenue pattern',
  base_headcount: 'Headcount',
  bill_rate: 'Bill rate',
  sow_reference: 'SOW reference',
  rationale: 'Rationale',
  renews_opportunity_id: 'Renews opportunity',
  is_removed: 'Removed',
}

export interface AuditFieldChange {
  key: string
  label: string
  from: string
  to: string
}

export interface AuditDiffLookups {
  stages?: Record<string, string>
  customers?: Record<string, string>
  owners?: Record<string, string>
  drivers?: Record<string, string>
  customFields?: Record<string, string>
  currency?: string
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a == null && b == null) return true
  if (typeof a === 'number' || typeof b === 'number') {
    return Number(a) === Number(b)
  }
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    return Boolean(a) === Boolean(b)
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    return JSON.stringify(a) === JSON.stringify(b)
  }
  return String(a ?? '') === String(b ?? '')
}

function looksLikeUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
}

function shortId(value: string): string {
  return value.length > 12 ? `${value.slice(0, 8)}…` : value
}

function formatScalar(key: string, value: unknown, lookups: AuditDiffLookups | undefined): string {
  if (value == null || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'

  const text = String(value)

  if (key === 'stage_id') {
    return lookups?.stages?.[text] ?? shortId(text)
  }
  if (key === 'customer_id') {
    return lookups?.customers?.[text] ?? shortId(text)
  }
  if (key === 'owner_id') {
    return lookups?.owners?.[text] ?? shortId(text)
  }
  if (key === 'forecast_driver_code') {
    return lookups?.drivers?.[text] ?? formatCodeLabel(text)
  }
  if (key === 'billing_type_code' || key === 'revenue_pattern_code') {
    return formatCodeLabel(text)
  }
  if (key === 'start_date' || key === 'end_date') {
    return formatDate(text)
  }
  if (key === 'bill_rate') {
    return formatMoney(text, lookups?.currency ?? 'USD')
  }
  if (key === 'renews_opportunity_id' && looksLikeUuid(text)) {
    return shortId(text)
  }
  if (looksLikeUuid(text) && key.endsWith('_id')) {
    return shortId(text)
  }
  return text
}

function fieldLabel(key: string, lookups?: AuditDiffLookups): string {
  if (key.startsWith('custom.')) {
    const customKey = key.slice('custom.'.length)
    return lookups?.customFields?.[customKey] ?? formatCodeLabel(customKey)
  }
  return FIELD_LABELS[key] ?? formatCodeLabel(key)
}

function flattenSnapshot(
  snapshot: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (!snapshot) return {}
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(snapshot)) {
    if (SKIP_KEYS.has(key)) continue
    if (key === 'custom_values' && isPlainObject(value)) {
      for (const [customKey, customValue] of Object.entries(value)) {
        out[`custom.${customKey}`] = customValue
      }
      continue
    }
    out[key] = value
  }
  return out
}

/** Compare before/after audit snapshots into labelled field changes. */
export function diffAuditSnapshots(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined,
  lookups?: AuditDiffLookups,
): AuditFieldChange[] {
  const left = flattenSnapshot(before)
  const right = flattenSnapshot(after)
  const keys = new Set([...Object.keys(left), ...Object.keys(right)])
  const changes: AuditFieldChange[] = []

  for (const key of keys) {
    const fromRaw = left[key]
    const toRaw = right[key]
    if (valuesEqual(fromRaw, toRaw)) continue
    // Creation: skip dumping every field; caller can still show action + actor.
    if (before == null && after != null) continue
    changes.push({
      key,
      label: fieldLabel(key, lookups),
      from: formatScalar(key, fromRaw, lookups),
      to: formatScalar(key, toRaw, lookups),
    })
  }

  const order = Object.keys(FIELD_LABELS)
  changes.sort((a, b) => {
    const ai = a.key.startsWith('custom.') ? order.length : order.indexOf(a.key)
    const bi = b.key.startsWith('custom.') ? order.length : order.indexOf(b.key)
    const aRank = ai === -1 ? order.length + 1 : ai
    const bRank = bi === -1 ? order.length + 1 : bi
    if (aRank !== bRank) return aRank - bRank
    return a.label.localeCompare(b.label)
  })

  return changes
}
